import React, { useContext, useEffect, useState, useCallback } from "react";
import styled, { keyframes, css } from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Collectible, TIER_ACCENTS, TierAccent } from "./types";
import AppContext from "../../Contexts/AppContext";
import UserContext from "../../Contexts/User";
import useCheckoutPayment from "./useCheckoutPayment";

/* ═══════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════ */
const accentFor = (tier: string): TierAccent =>
  TIER_ACCENTS[tier] ?? TIER_ACCENTS.Common;

type PayMethod = "crypto" | "card" | "paypal";
type CryptoCoin = "USDT" | "ETH" | "BNB" | "TON" | "SOL" | "More";

const PAY_CFG: Record<PayMethod, { enabled: boolean }> = {
  crypto: { enabled: true },
  card: { enabled: false },
  paypal: { enabled: false },
};

const COIN_COLORS: Record<string, string> = {
  USDT: "#26a17b",
  ETH: "#627eea",
  BNB: "#f3ba2f",
  TON: "#0098ea",
  SOL: "#9945ff",
  More: "#888",
};

const TRUST_ITEMS = [
  "Secure checkout",
  "Card appears in My Space after purchase",
  "Vcoin & Power credited from our database automatically",
  "Supply count updates in real time",
];

/* ═══════════════════════════════════════════════════════
   ANIMATIONS
═══════════════════════════════════════════════════════ */
const flashAnim = keyframes`
  0%   { opacity: .3; transform: scale(.92); }
  100% { opacity: 1;  transform: scale(1); }
`;

const bumpAnim = keyframes`
  0%   { transform: scale(1); }
  50%  { transform: scale(1.07); }
  100% { transform: scale(1); }
`;

const popAnim = keyframes`
  0%   { transform: scale(1); }
  50%  { transform: scale(1.22); }
  100% { transform: scale(1); }
`;

/* ═══════════════════════════════════════════════════════
   STYLED COMPONENTS — PAGE LAYOUT
═══════════════════════════════════════════════════════ */
const Page = styled.div`
  width: 100%;
  max-width: 1140px;
  margin: 0 auto;
  padding: 14px 14px 100px;
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  color: #f0f2ff;
  -webkit-font-smoothing: antialiased;

  @media (min-width: 901px) {
    padding-bottom: 40px;
  }
  @media (max-width: 900px) {
    padding-bottom: 20px;
  }
`;

const Shell = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

/* Glass panel base */
const Panel = styled.div<{ $a: TierAccent }>`
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background: linear-gradient(
    155deg,
    rgba(255, 255, 255, 0.058),
    rgba(255, 255, 255, 0.02)
  );
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.044) 0%,
      transparent 28%
    );
  }

  @media (max-width: 480px) {
    border-radius: 20px;
  }
`;

/* ═══════════════════════════════════════════════════════
   LEFT PANEL — CARD FACE
═══════════════════════════════════════════════════════ */
const ImgPanel = styled(Panel)`
  padding: 13px;
  @media (max-width: 900px) { padding: 10px; }
  @media (max-width: 480px) { padding: 8px; }
`;

const CardFace = styled.div<{ $a: TierAccent }>`
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  aspect-ratio: 0.78 / 1;
  min-height: 300px;
  background: #0d0d14;
  border: 2px solid ${(p) => p.$a.line};
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.04) inset,
    0 22px 48px rgba(0, 0, 0, 0.4), 0 0 52px ${(p) => p.$a.glow};

  &::before {
    content: "";
    position: absolute;
    inset: 9px;
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.11);
    pointer-events: none;
    z-index: 3;
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    background: linear-gradient(
        180deg,
        rgba(8, 8, 14, 0.42) 0%,
        rgba(8, 8, 14, 0.08) 20%,
        rgba(8, 8, 14, 0) 34%
      ),
      linear-gradient(
        180deg,
        rgba(8, 8, 14, 0) 40%,
        rgba(8, 8, 14, 0.18) 55%,
        rgba(8, 8, 14, 0.6) 72%,
        rgba(8, 8, 14, 0.98) 100%
      );
  }

  @media (max-width: 900px) {
    min-height: 0;
    aspect-ratio: 0.78 / 1;
  }
  @media (max-width: 680px) { border-radius: 18px; }
  @media (max-width: 480px) {
    border-radius: 16px;
    &::before { inset: 7px; border-radius: 11px; }
  }
`;

const CardArt = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background: #0d0d14;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transform: scale(1.04);
    filter: saturate(1.06) contrast(1.03);
    transition: opacity 0.3s;
  }
`;

const CardInner = styled.div`
  position: relative;
  z-index: 4;
  height: 100%;
  padding: 14px;
  display: flex;
  flex-direction: column;

  @media (max-width: 680px) { padding: 12px; }
  @media (max-width: 480px) { padding: 11px; }
`;

const CardTopbar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const Chip = styled.div<{ $variant: "tier" | "price"; $a: TierAccent }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding: 0 13px;
  border-radius: 999px;
  border: 1px solid
    ${(p) =>
      p.$variant === "tier"
        ? p.$a.line
        : "rgba(255,255,255,.15)"};
  background: rgba(8, 9, 16, 0.8);
  backdrop-filter: blur(10px);
  font-size: 10.5px;
  line-height: 1;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.13em;
  white-space: nowrap;
  text-shadow: -1px -1px 0 rgba(0, 0, 0, 0.7), 1px 1px 0 rgba(0, 0, 0, 0.7);
  color: ${(p) => (p.$variant === "tier" ? p.$a.accent2 : "#fff")};

  @media (max-width: 900px) { min-height: 30px; padding: 0 11px; font-size: 9.5px; }
  @media (max-width: 480px) { min-height: 27px; padding: 0 10px; font-size: 8.5px; }
  @media (max-width: 380px) { font-size: 7.5px; min-height: 24px; padding: 0 8px; }
`;

const CardBottom = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 9px;
`;

const VcoinWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const VcoinLbl = styled.div`
  color: #fff;
  font-size: clamp(10px, 0.9vw, 14px);
  line-height: 1;
  font-weight: 900;
  text-shadow: -2px -2px 0 rgba(0, 0, 0, 0.95),
    2px 2px 0 rgba(0, 0, 0, 0.95);
`;

const VcoinNum = styled.div<{ $a: TierAccent; $bump?: boolean }>`
  color: ${(p) => p.$a.accent2};
  font-size: clamp(28px, 3.4vw, 62px);
  line-height: 0.9;
  letter-spacing: -0.07em;
  font-weight: 900;
  text-shadow: -3px -3px 0 rgba(0, 0, 0, 0.97),
    3px 3px 0 rgba(0, 0, 0, 0.97), 0 8px 18px rgba(0, 0, 0, 0.55);
  ${(p) => p.$bump && css`animation: ${bumpAnim} 0.22s ease;`}

  @media (max-width: 900px) { font-size: clamp(32px, 9vw, 56px); }
  @media (max-width: 680px) { font-size: clamp(28px, 8vw, 46px); }
  @media (max-width: 480px) { font-size: clamp(24px, 7vw, 40px); }
  @media (max-width: 380px) { font-size: clamp(20px, 6.5vw, 32px); }
`;

const BannerStack = styled.div`
  display: grid;
  gap: 7px;
`;

const Banner = styled.div`
  min-height: 50px;
  border-radius: 13px;
  padding: 8px 11px;
  display: flex;
  align-items: center;
  gap: 9px;
  background: rgba(8, 9, 16, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(10px);

  @media (max-width: 900px) { min-height: 52px; }
  @media (max-width: 480px) { min-height: 46px; padding: 7px 9px; gap: 8px; border-radius: 11px; }
  @media (max-width: 380px) { min-height: 42px; padding: 6px 8px; }
`;

const BIcon = styled.div<{ $a: TierAccent }>`
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${(p) => p.$a.line};
  color: ${(p) => p.$a.accent2};
  font-size: 13px;
  box-shadow: 0 0 12px ${(p) => p.$a.glow};

  @media (max-width: 480px) { width: 24px; height: 24px; flex: 0 0 24px; font-size: 11px; }
`;

const BCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  span {
    font-size: 8.5px;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    font-weight: 900;
    color: rgba(255, 255, 255, 0.85);
    text-shadow: -1px -1px 0 rgba(0, 0, 0, 0.65),
      1px 1px 0 rgba(0, 0, 0, 0.65);
  }
  strong {
    font-size: clamp(11px, 1vw, 15px);
    font-weight: 900;
    color: #fff;
    line-height: 1.1;
    text-shadow: -1px -1px 0 rgba(0, 0, 0, 0.9),
      1px 1px 0 rgba(0, 0, 0, 0.9);
    transition: color 0.2s;
  }

  @media (max-width: 900px) { strong { font-size: clamp(13px, 3.5vw, 16px); } }
  @media (max-width: 480px) { strong { font-size: clamp(11px, 3vw, 14px); } }
  @media (max-width: 380px) { strong { font-size: 10.5px; } }
`;

const CardName = styled.div`
  color: #fff;
  font-size: clamp(20px, 2vw, 34px);
  line-height: 1;
  letter-spacing: -0.05em;
  font-weight: 900;
  text-shadow: -2px -2px 0 rgba(0, 0, 0, 0.95),
    2px 2px 0 rgba(0, 0, 0, 0.95);

  @media (max-width: 900px) { font-size: clamp(22px, 6vw, 32px); }
  @media (max-width: 680px) { font-size: clamp(20px, 5.5vw, 28px); }
  @media (max-width: 480px) { font-size: clamp(17px, 5vw, 24px); }
  @media (max-width: 380px) { font-size: clamp(15px, 4.5vw, 20px); }
`;

const ThumbRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 11px 4px 0;
  flex-wrap: wrap;

  @media (max-width: 900px) { padding: 10px 4px 0; }
`;

const ThumbBtn = styled.button<{ $active: boolean; $a: TierAccent }>`
  width: 54px;
  height: 54px;
  border-radius: 12px;
  border: 2px solid
    ${(p) =>
      p.$active ? p.$a.accent : "rgba(255,255,255,.10)"};
  overflow: hidden;
  cursor: pointer;
  background: #111;
  transition: 0.2s ease;
  flex: 0 0 auto;
  padding: 0;
  ${(p) =>
    p.$active &&
    css`
      box-shadow: 0 0 0 3px ${p.$a.glow};
      transform: translateY(-2px);
    `}

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: 900px) { width: 52px; height: 52px; }
  @media (max-width: 680px) { width: 46px; height: 46px; border-radius: 10px; }
  @media (max-width: 480px) { width: 42px; height: 42px; border-radius: 9px; }
`;

/* ═══════════════════════════════════════════════════════
   RIGHT PANEL — CHECKOUT
═══════════════════════════════════════════════════════ */
const CoPanel = styled(Panel)`
  padding: 20px 18px 18px;

  @media (max-width: 900px) { padding: 16px 14px 16px; }
  @media (max-width: 680px) { padding: 14px 12px 14px; }
  @media (max-width: 480px) { padding: 13px 11px 13px; }
`;

const TierBadge = styled.div<{ $a: TierAccent }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid ${(p) => p.$a.line};
  background: rgba(255, 255, 255, 0.04);
  color: ${(p) => p.$a.accent2};
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  margin-bottom: 8px;

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${(p) => p.$a.accent};
    box-shadow: 0 0 8px ${(p) => p.$a.glow};
  }
`;

const ProductTitle = styled.h1`
  font-size: clamp(22px, 3vw, 34px);
  line-height: 1.02;
  letter-spacing: -0.046em;
  font-weight: 900;
  margin: 0 0 7px;
  color: #f0f2ff;

  @media (max-width: 680px) { font-size: clamp(20px, 5vw, 28px); }
`;

/* availability */
const AvailRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5px;
  font-size: 11.5px;
  font-weight: 700;
  color: rgba(240, 242, 255, 0.72);
  flex-wrap: wrap;

  strong {
    color: #fff;
  }
`;

const AvailBar = styled.div`
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
  margin-bottom: 14px;
  overflow: hidden;
`;

const AvailFill = styled.div<{ $pct: number; $a: TierAccent }>`
  height: 100%;
  border-radius: 999px;
  width: ${(p) => p.$pct}%;
  background: linear-gradient(90deg, ${(p) => p.$a.accent}, ${(p) => p.$a.accent2});
  box-shadow: 0 0 8px ${(p) => p.$a.glow};
  transition: width 0.4s ease;
`;

/* ── ORDER SUMMARY ── */
const OrderBox = styled.div`
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background: rgba(255, 255, 255, 0.046);
  margin-bottom: 11px;
  overflow: hidden;
`;

const OrderHead = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 11px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);

  @media (max-width: 480px) { padding: 10px 12px; gap: 9px; }
`;

const OrderThumb = styled.div<{ $a: TierAccent }>`
  width: 36px;
  height: 46px;
  border-radius: 8px;
  overflow: hidden;
  border: 1.5px solid ${(p) => p.$a.line};
  flex: 0 0 auto;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const OrderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const OrderName = styled.div`
  font-size: 14px;
  font-weight: 900;
  letter-spacing: -0.03em;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const OrderTier = styled.div<{ $a: TierAccent }>`
  font-size: 9.5px;
  font-weight: 800;
  color: ${(p) => p.$a.accent2};
  text-transform: uppercase;
  letter-spacing: 0.12em;
`;

const OrderRows = styled.div`
  padding: 3px 0;
`;

const ORow = styled.div<{ $total?: boolean; $totalVcoin?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 14px;
  gap: 12px;

  & + & {
    border-top: 1px solid rgba(255, 255, 255, 0.055);
  }

  @media (max-width: 480px) { padding: 8px 12px; }
`;

const OLbl = styled.span<{ $total?: boolean }>`
  font-size: ${(p) => (p.$total ? "13px" : "12.5px")};
  font-weight: ${(p) => (p.$total ? 800 : 700)};
  color: ${(p) => (p.$total ? "#fff" : "rgba(240,242,255,.72)")};
`;

const OVal = styled.span<{ $gold?: boolean; $total?: boolean; $a?: TierAccent; $flash?: boolean }>`
  font-size: ${(p) => (p.$total ? "22px" : "13.5px")};
  font-weight: 900;
  color: ${(p) =>
    p.$gold
      ? (p.$a?.accent2 ?? "#fff0ad")
      : p.$total
      ? (p.$a?.accent ?? "#f5c842")
      : "#fff"};
  text-align: right;
  transition: all 0.2s ease;
  letter-spacing: ${(p) => (p.$total ? "-0.05em" : "0")};
  ${(p) => p.$flash && css`animation: ${flashAnim} 0.2s ease forwards;`}

  @media (max-width: 480px) {
    ${(p) => p.$total && "font-size: 20px;"}
  }
`;

const ODivider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 2px 14px;
`;

/* ── BOOST NOTE ── */
const BoostNote = styled.div<{ $a: TierAccent }>`
  border-radius: 13px;
  border: 1px solid ${(p) => p.$a.line};
  background: ${(p) => p.$a.accent}0a;
  padding: 9px 13px;
  margin-bottom: 11px;
  font-size: 11.5px;
  color: rgba(240, 242, 255, 0.72);
  font-weight: 600;
  line-height: 1.55;

  strong {
    color: ${(p) => p.$a.accent2};
  }
`;

/* ── QUANTITY ── */
const QtyWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 13px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background: rgba(255, 255, 255, 0.046);
  margin-bottom: 12px;
`;

const QtyLbl = styled.span`
  font-size: 12.5px;
  font-weight: 800;
  color: rgba(240, 242, 255, 0.72);
`;

const QtyControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const QtyBtn = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 2px solid rgba(255, 255, 255, 0.16);
  background: transparent;
  color: #fff;
  font-size: 22px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.18s ease;
  line-height: 1;
  user-select: none;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
  &:active {
    background: rgba(255, 255, 255, 0.14);
    transform: scale(0.92);
  }
  &:disabled {
    opacity: 0.25;
    cursor: not-allowed;
  }
`;

const QtyNum = styled.div<{ $pop?: boolean }>`
  min-width: 44px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 20px;
  font-weight: 900;
  letter-spacing: -0.03em;
  display: flex;
  align-items: center;
  justify-content: center;
  ${(p) => p.$pop && css`animation: ${popAnim} 0.2s ease;`}
`;

/* ── MOBILE CTA ── */
const MobileCta = styled.button<{ $a: TierAccent }>`
  display: none;
  width: 100%;
  min-height: 54px;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, ${(p) => p.$a.accent}, ${(p) => p.$a.accent2});
  color: #111;
  font-size: 15.5px;
  font-weight: 900;
  letter-spacing: -0.02em;
  cursor: pointer;
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.2), 0 8px 22px ${(p) => p.$a.glow};
  transition: transform 0.22s ease, box-shadow 0.22s;
  align-items: center;
  justify-content: center;
  gap: 9px;
  margin-bottom: 14px;

  &:hover { transform: translateY(-2px); }
  &:active { transform: scale(0.97); }
  &:disabled { opacity: 0.6; cursor: wait; transform: none; }

  @media (max-width: 900px) {
    display: flex !important;
  }
  @media (min-width: 901px) {
    display: none !important;
  }
`;

/* ── PAYMENT TABS ── */
const SecLbl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  color: rgba(240, 242, 255, 0.44);
  font-size: 9.5px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.14em;

  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.07);
  }
`;

const PayTabs = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 10px;

  @media (max-width: 680px) { gap: 6px; }
  @media (max-width: 480px) { gap: 5px; }
`;

const PayTab = styled.button<{ $active: boolean; $soon: boolean; $a: TierAccent }>`
  min-height: 68px;
  border-radius: 14px;
  border: 2px solid
    ${(p) =>
      p.$active ? p.$a.line : "rgba(255,255,255,.09)"};
  background: ${(p) =>
    p.$active
      ? "rgba(255,255,255,.06)"
      : "rgba(255,255,255,.022)"};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  cursor: ${(p) => (p.$soon ? "not-allowed" : "pointer")};
  transition: 0.2s ease;
  padding: 8px 5px;
  position: relative;
  opacity: ${(p) => (p.$soon ? 0.5 : 1)};
  color: #f0f2ff;
  ${(p) =>
    p.$active &&
    css`box-shadow: 0 0 0 3px ${p.$a.glow};`}

  &:hover {
    ${(p) =>
      !p.$soon &&
      css`
        background: rgba(255, 255, 255, 0.055);
        transform: translateY(-1px);
      `}
  }
`;

const SoonPill = styled.span`
  position: absolute;
  top: -9px;
  right: -3px;
  padding: 2px 7px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 7.5px;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(240, 242, 255, 0.44);
  white-space: nowrap;
`;

const PtIcon = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;

  @media (max-width: 480px) { width: 30px; height: 30px; }
`;

const PtName = styled.div`
  font-size: 11.5px;
  font-weight: 900;
  text-align: center;
  line-height: 1.2;

  @media (max-width: 680px) { font-size: 10.5px; }
  @media (max-width: 480px) { font-size: 10px; }
`;

const PtSub = styled.div<{ $active?: boolean; $a?: TierAccent }>`
  font-size: 9px;
  color: ${(p) => (p.$active ? p.$a?.accent2 ?? "#fff0ad" : "rgba(240,242,255,.44)")};
  font-weight: 700;
`;

/* ── CRYPTO COINS ── */
const CryptoBlock = styled.div<{ $open: boolean }>`
  max-height: ${(p) => (p.$open ? "140px" : "0")};
  opacity: ${(p) => (p.$open ? 1 : 0)};
  overflow: hidden;
  margin-bottom: ${(p) => (p.$open ? "10px" : "0")};
  transition: max-height 0.32s ease, opacity 0.24s ease, margin 0.24s ease;
`;

const CoinGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 7px;
  padding: 2px 0;
`;

const CoinBtn = styled.button<{ $active: boolean; $color: string; $a: TierAccent }>`
  min-height: 40px;
  border-radius: 999px;
  border: 2px solid
    ${(p) =>
      p.$active ? p.$a.line : "rgba(255,255,255,.10)"};
  background: ${(p) =>
    p.$active
      ? "rgba(255,255,255,.055)"
      : "rgba(255,255,255,.02)"};
  color: ${(p) =>
    p.$active ? p.$a.accent2 : "rgba(240,242,255,.72)"};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-weight: 900;
  font-size: 11.5px;
  cursor: pointer;
  transition: 0.18s ease;
  padding: 0 8px;
  ${(p) =>
    p.$active &&
    css`box-shadow: 0 0 0 3px ${p.$a.glow};`}

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    transform: translateY(-1px);
  }
`;

const CoinDot = styled.span<{ $color: string; $active: boolean; $a: TierAccent }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: ${(p) => (p.$active ? p.$a.accent : p.$color)};
`;

/* ── PAY INFO (coming soon panels) ── */
const PayInfo = styled.div<{ $show: boolean }>`
  display: ${(p) => (p.$show ? "block" : "none")};
  margin-bottom: 10px;
  padding: 10px 13px;
  border-radius: 13px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.02);
  font-size: 12.5px;
  color: rgba(240, 242, 255, 0.72);
  font-weight: 600;
  line-height: 1.55;
  text-align: center;
`;

/* ── TRUST LIST ── */
const TrustList = styled.div`
  display: grid;
  gap: 6px;
  margin-bottom: 14px;
`;

const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #fff;
  font-weight: 700;
`;

const TIcon = styled.div<{ $a: TierAccent }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex: 0 0 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid ${(p) => p.$a.line};
  background: rgba(255, 255, 255, 0.03);
  color: ${(p) => p.$a.accent2};
  font-size: 8.5px;
`;

/* ── DESKTOP CTA ── */
const PanelCta = styled.button<{ $a: TierAccent }>`
  width: 100%;
  min-height: 56px;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, ${(p) => p.$a.accent}, ${(p) => p.$a.accent2});
  color: #111;
  font-size: 16px;
  font-weight: 900;
  letter-spacing: -0.02em;
  cursor: pointer;
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.2), 0 8px 22px ${(p) => p.$a.glow};
  transition: transform 0.22s ease, box-shadow 0.22s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 34px rgba(0, 0, 0, 0.22), 0 10px 28px ${(p) => p.$a.glow};
  }
  &:active {
    transform: scale(0.97);
  }
  &:disabled { opacity: 0.6; cursor: wait; transform: none; }

  @media (max-width: 900px) {
    display: none !important;
  }
`;

/* ═══════════════════════════════════════════════════════
   SUCCESS MODAL
═══════════════════════════════════════════════════════ */
const Backdrop = styled.div<{ $show: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.68);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: ${(p) => (p.$show ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 9999;
`;

const ModalBox = styled.div`
  width: 100%;
  max-width: 470px;
  border-radius: 26px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(
    155deg,
    rgba(24, 12, 48, 0.98),
    rgba(9, 5, 18, 0.98)
  );
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.6);
  padding: 24px 20px 22px;
  position: relative;
  overflow: hidden;
  color: #f0f2ff;

  &::before {
    content: "";
    position: absolute;
    top: -70px;
    left: 50%;
    transform: translateX(-50%);
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(245, 200, 66, 0.13),
      transparent 70%
    );
    pointer-events: none;
  }
`;

const MClose = styled.button`
  position: absolute;
  top: 13px;
  right: 13px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(240, 242, 255, 0.44);
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.18s;

  &:hover {
    background: rgba(255, 255, 255, 0.09);
  }
`;

const MHead = styled.div`
  text-align: center;
  margin-bottom: 14px;
`;

const MEmoji = styled.div`
  font-size: 34px;
  margin-bottom: 5px;
`;

const MTitle = styled.div`
  font-size: 21px;
  font-weight: 900;
  letter-spacing: -0.04em;
  margin-bottom: 3px;
`;

const MSub = styled.div`
  font-size: 13px;
  color: rgba(240, 242, 255, 0.72);
  font-weight: 600;
`;

const MCardWrap = styled.div<{ $a: TierAccent }>`
  width: 128px;
  aspect-ratio: 0.78 / 1;
  border-radius: 16px;
  overflow: hidden;
  border: 2px solid ${(p) => p.$a.line};
  box-shadow: 0 0 26px ${(p) => p.$a.glow}, 0 14px 30px rgba(0, 0, 0, 0.4);
  margin: 0 auto 15px;
  position: relative;
`;

const MCardArt = styled.div`
  position: absolute;
  inset: 0;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const MCardOv = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 42%, rgba(6, 6, 14, 0.9) 100%);
  display: flex;
  align-items: flex-end;
  padding: 9px;
`;

const MCardName = styled.div`
  font-size: 12px;
  font-weight: 900;
  color: #fff;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.8);
`;

const MRewards = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 14px;
`;

const MReward = styled.div<{ $a: TierAccent }>`
  border-radius: 13px;
  border: 1px solid ${(p) => p.$a.line};
  background: rgba(255, 255, 255, 0.04);
  padding: 10px 11px;
  text-align: center;
`;

const MRLbl = styled.div`
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: rgba(240, 242, 255, 0.44);
  margin-bottom: 4px;
`;

const MRVal = styled.div<{ $a: TierAccent }>`
  font-size: 17px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: ${(p) => p.$a.accent2};
`;

const MCopy = styled.div`
  text-align: center;
  font-size: 12.5px;
  color: rgba(240, 242, 255, 0.72);
  font-weight: 600;
  line-height: 1.55;
  margin-bottom: 15px;
`;

const MActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  @media (max-width: 380px) { grid-template-columns: 1fr; }
`;

const MBtn = styled.button<{ $primary?: boolean; $a?: TierAccent }>`
  min-height: 48px;
  border-radius: 12px;
  border: 2px solid
    ${(p) =>
      p.$primary ? "transparent" : "rgba(255,255,255,.10)"};
  background: ${(p) =>
    p.$primary
      ? `linear-gradient(135deg, ${p.$a?.accent ?? "#f5c842"}, ${p.$a?.accent2 ?? "#fff0ad"})`
      : "rgba(255,255,255,.05)"};
  color: ${(p) => (p.$primary ? "#111" : "#fff")};
  font-size: 13px;
  font-weight: 900;
  cursor: pointer;
  transition: 0.18s;
  padding: 0 10px;
  ${(p) =>
    p.$primary &&
    css`box-shadow: 0 10px 22px ${p.$a?.glow ?? "rgba(245,200,66,.30)"};`}

  &:hover {
    ${(p) =>
      p.$primary
        ? "filter: brightness(1.06);"
        : "background: rgba(255,255,255,.09); transform: translateY(-1px);"}
  }
`;

/* ═══════════════════════════════════════════════════════
   SVG ICONS (inline to match HTML)
═══════════════════════════════════════════════════════ */
const CryptoIcon = () => (
  <svg viewBox="0 0 40 40" width="32" height="32" fill="none">
    <circle cx="20" cy="20" r="19" fill="#c0c0c0" stroke="rgba(255,255,255,.18)" strokeWidth="1" />
    <circle cx="20" cy="20" r="14" fill="#a8a8a8" />
    <circle cx="20" cy="20" r="10" fill="#d0d0d0" />
    <ellipse cx="20" cy="20" rx="7" ry="7" fill="#b8b8b8" />
    <path d="M20 13 Q24 16 24 20 Q24 24 20 27 Q16 24 16 20 Q16 16 20 13Z" fill="rgba(255,255,255,.18)" stroke="rgba(255,255,255,.3)" strokeWidth=".6" />
    <path d="M13 20 Q16 17 20 16 Q24 17 27 20 Q24 23 20 24 Q16 23 13 20Z" fill="rgba(255,255,255,.12)" stroke="rgba(255,255,255,.25)" strokeWidth=".6" />
    <circle cx="20" cy="20" r="3" fill="rgba(255,255,255,.55)" />
    <ellipse cx="16" cy="15" rx="3.5" ry="2" fill="rgba(255,255,255,.38)" transform="rotate(-30,16,15)" />
  </svg>
);

const CardIcon = () => (
  <svg viewBox="0 0 40 28" width="38" height="27" fill="none">
    <rect x="1" y="1" width="38" height="26" rx="4" fill="#2a2a38" stroke="rgba(255,255,255,.18)" strokeWidth="1.2" />
    <rect x="1" y="7" width="38" height="6" fill="rgba(255,255,255,.14)" />
    <rect x="6" y="17" width="14" height="3" rx="1.5" fill="rgba(255,255,255,.28)" />
    <rect x="6" y="17" width="4" height="3" rx="1.5" fill="rgba(255,255,255,.55)" />
    <rect x="28" y="16" width="6" height="5" rx="1" fill="#f5c842" opacity=".85" />
    <rect x="25" y="16" width="6" height="5" rx="1" fill="#ffae46" opacity=".55" />
  </svg>
);

const PaypalIcon = () => (
  <svg viewBox="0 0 36 40" width="28" height="31" fill="none">
    <ellipse cx="18" cy="27" rx="14" ry="12" fill="#f5c842" />
    <ellipse cx="18" cy="27" rx="11" ry="9.5" fill="#f5d060" />
    <rect x="13" y="14" width="10" height="6" rx="3" fill="#e8b830" />
    <ellipse cx="18" cy="13" rx="5" ry="3.5" fill="#d4a520" />
    <text x="18" y="32" textAnchor="middle" fontSize="12" fontWeight="900" fill="#111" fontFamily="Arial,sans-serif">$</text>
    <ellipse cx="12" cy="22" rx="3" ry="2" fill="rgba(255,255,255,.32)" transform="rotate(-20,12,22)" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

/* ═══════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════ */
const fmt = (n: number) => Number(n).toLocaleString("en-US");

/* ═══════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════ */
const BuyGetCheckout: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { setBuyProduct } = useContext(AppContext);
  const { userInfo } = useContext(UserContext);

  const payment = useCheckoutPayment();

  const [item, setItem] = useState<Collectible | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImg, setSelectedImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [method, setMethod] = useState<PayMethod>("crypto");
  const [coin, setCoin] = useState<CryptoCoin>("USDT");
  const [flashKey, setFlashKey] = useState(0);
  const [popKey, setPopKey] = useState(0);
  const [bumpKey, setBumpKey] = useState(0);

  /* fetch product */
  useEffect(() => {
    const fetchItem = async () => {
      if (!slug) return;
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_V2E}getAllProductsFromDatabase`
        );
        const products: any[] = res?.data?.data || [];
        const p = products.find((prod: any) => String(prod.id) === slug);
        if (p) {
          setItem({
            id: String(p.id),
            slug: String(p.id),
            name: p.name ?? "",
            tier: p.rarity ?? "Common",
            cardClass: p.rarity ?? "",
            unitPrice: p.priceUsd ?? 0,
            unitVcoin: p.vCoin ?? 0,
            powerMult: 1,
            powerUses: 0,
            totalSupply: p.totalSupply ?? 0,
            remainingSupply: p.remaining ?? 0,
            supplyDays: 7,
            images: p.image_link ? [p.image_link] : [],
            isMystery:
              p.name === "Mystery Box" || p.name === "Gold Mystery Box",
            isActive: true,
            productId: p.id,
          });
        }
      } catch {
        /* silently handle */
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [slug]);

  /* sync PayAmount to localStorage & context */
  useEffect(() => {
    if (!item) return;
    const totalPrice = item.unitPrice * qty;
    const numericId = item.productId ?? item.id;
    const payvalue = [totalPrice, "PRODUCT_PURCHASE", 0, 0, numericId];
    localStorage.setItem("PayAmount", JSON.stringify(payvalue));
    setBuyProduct({
      id: numericId,
      name: item.name,
      priceUsd: item.unitPrice,
      remaining: item.remainingSupply,
      totalSupply: item.totalSupply,
      vCoin: item.unitVcoin,
      image_link: item.images?.[0] ?? "",
      rarity: item.tier,
    });
  }, [item, qty, setBuyProduct]);

  /* qty change triggers flash & bump */
  const changeQty = useCallback(
    (delta: number) => {
      if (!item) return;
      setQty((q) => {
        const next = Math.max(1, Math.min(item.remainingSupply, q + delta));
        if (next !== q) {
          setFlashKey((k) => k + 1);
          setPopKey((k) => k + 1);
          setBumpKey((k) => k + 1);
        }
        return next;
      });
    },
    [item]
  );

  const handleCheckout = useCallback(() => {
    if (!userInfo?.uid) {
      localStorage.setItem("redirectUrl", window.location.pathname);
      navigate("/onekey");
      return;
    }
    if (!item) return;
    const numericId = item.productId ?? item.id;
    if (method === "crypto") {
      payment.pay(coin, item.unitPrice * qty, numericId, qty);
    } else if (method === "card") {
      payment.payWithCard(item.unitPrice * qty, numericId, qty);
    }
  }, [userInfo, navigate, item, method, coin, qty, payment]);

  if (loading) {
    return (
      <Page style={{ textAlign: "center", paddingTop: 120 }}>
        <p style={{ color: "rgba(240,242,255,.72)" }}>Loading...</p>
      </Page>
    );
  }

  if (!item) {
    return (
      <Page style={{ textAlign: "center", paddingTop: 120 }}>
        <p style={{ color: "rgba(240,242,255,.72)" }}>
          Collectible not found.
        </p>
      </Page>
    );
  }

  const a = accentFor(item.tier);
  const totalPrice = item.unitPrice * qty;
  const totalVcoin = item.unitVcoin * qty;
  const totalPower = item.powerUses * qty;
  const supplyPct = (item.remainingSupply / item.totalSupply) * 100;
  const mainImgSrc = item.images?.[selectedImg] ?? "";
  const coinLabel =
    method === "crypto"
      ? coin
      : method === "card"
      ? "Credit Card"
      : "PayPal";

  const COINS: CryptoCoin[] = ["USDT", "ETH", "BNB", "TON", "SOL", "More"];

  return (
    <>
      <Page>
        <Shell>
          {/* ════════ LEFT — CARD ════════ */}
          <ImgPanel $a={a}>
            <CardFace $a={a}>
              <CardArt>
                <img src={mainImgSrc} alt="Card art" />
              </CardArt>
              <CardInner>
                <CardTopbar>
                  <Chip $variant="tier" $a={a}>
                    {item.tier}
                  </Chip>
                  <Chip $variant="price" $a={a}>
                    ${item.unitPrice}
                  </Chip>
                </CardTopbar>
                <CardBottom>
                  <VcoinWrap>
                    <VcoinLbl>Total Vcoin</VcoinLbl>
                    <VcoinNum key={`bump-${bumpKey}`} $a={a} $bump={bumpKey > 0}>
                      {fmt(totalVcoin)}
                    </VcoinNum>
                  </VcoinWrap>
                  <BannerStack>
                    <Banner>
                      <BIcon $a={a}>&#x26A1;</BIcon>
                      <BCopy>
                        <span>Power</span>
                        <strong>
                          x{item.powerMult} &middot; {fmt(totalPower)} uses
                        </strong>
                      </BCopy>
                    </Banner>
                    <Banner>
                      <BIcon $a={a}>&#x25C8;</BIcon>
                      <BCopy>
                        <span>Remaining</span>
                        <strong>{fmt(item.remainingSupply)} left</strong>
                      </BCopy>
                    </Banner>
                  </BannerStack>
                  <CardName>{item.name}</CardName>
                </CardBottom>
              </CardInner>
            </CardFace>

            {item.images.length > 1 && (
              <ThumbRow>
                {item.images.map((src, i) => (
                  <ThumbBtn
                    key={i}
                    $active={i === selectedImg}
                    $a={a}
                    onClick={() => setSelectedImg(i)}
                  >
                    <img src={src} alt="" />
                  </ThumbBtn>
                ))}
              </ThumbRow>
            )}
          </ImgPanel>

          {/* ════════ RIGHT — CHECKOUT ════════ */}
          <CoPanel $a={a}>
            <TierBadge $a={a}>{item.tier}</TierBadge>
            <ProductTitle>{item.name}</ProductTitle>

            <AvailRow>
              <span>
                Supply: <strong>{fmt(item.totalSupply)}</strong>
              </span>
              <span>
                Remaining: <strong>{fmt(item.remainingSupply)}</strong>
              </span>
            </AvailRow>
            <AvailBar>
              <AvailFill $pct={supplyPct} $a={a} />
            </AvailBar>

            {/* ORDER SUMMARY */}
            <OrderBox>
              <OrderHead>
                <OrderThumb $a={a}>
                  <img src={mainImgSrc} alt="" />
                </OrderThumb>
                <OrderInfo>
                  <OrderName>{item.name}</OrderName>
                  <OrderTier $a={a}>{item.tier}</OrderTier>
                </OrderInfo>
              </OrderHead>
              <OrderRows>
                <ORow>
                  <OLbl>Per card price</OLbl>
                  <OVal>${item.unitPrice}</OVal>
                </ORow>
                <ORow>
                  <OLbl>Per card Vcoin</OLbl>
                  <OVal $gold $a={a}>
                    {fmt(item.unitVcoin)}
                  </OVal>
                </ORow>
                <ORow>
                  <OLbl>Per card power</OLbl>
                  <OVal $gold $a={a}>
                    x{item.powerMult} &middot; {item.powerUses} uses
                  </OVal>
                </ORow>
                <ODivider />
                <ORow $total>
                  <OLbl $total>Total price</OLbl>
                  <OVal key={`tp-${flashKey}`} $total $a={a} $flash={flashKey > 0}>
                    ${fmt(totalPrice)}
                  </OVal>
                </ORow>
                <ORow $total $totalVcoin>
                  <OLbl $total>Total Vcoin</OLbl>
                  <OVal key={`tv-${flashKey}`} $total $gold $a={a} $flash={flashKey > 0}>
                    {fmt(totalVcoin)}
                  </OVal>
                </ORow>
                <ORow $total>
                  <OLbl $total>Total power uses</OLbl>
                  <OVal key={`tpw-${flashKey}`} $total $gold $a={a} $flash={flashKey > 0}>
                    {fmt(totalPower)} uses
                  </OVal>
                </ORow>
              </OrderRows>
            </OrderBox>

            {/* BOOST NOTE */}
            <BoostNote $a={a}>
              &#x2728; <strong>Social Boost</strong> eligible &mdash; share with
              50+ followers for up to{" "}
              <strong>{fmt(totalVcoin * 2)} Vcoin</strong> (2&times; reward).
            </BoostNote>

            {/* QUANTITY */}
            <QtyWrap>
              <QtyLbl>Quantity</QtyLbl>
              <QtyControls>
                <QtyBtn disabled={qty <= 1} onClick={() => changeQty(-1)}>
                  &#x2212;
                </QtyBtn>
                <QtyNum key={`pop-${popKey}`} $pop={popKey > 0}>
                  {qty}
                </QtyNum>
                <QtyBtn
                  disabled={qty >= item.remainingSupply}
                  onClick={() => changeQty(1)}
                >
                  &#x2B;
                </QtyBtn>
              </QtyControls>
            </QtyWrap>

            {/* MOBILE INLINE BUY */}
            <MobileCta $a={a} onClick={handleCheckout} disabled={payment.busy}>
              <span>
                {!userInfo?.uid
                  ? `Login to Purchase \u00B7 $${fmt(totalPrice)}`
                  : payment.busy
                  ? "Processing..."
                  : method === "crypto" && !payment.isWalletConnected
                  ? `Connect Wallet \u00B7 $${fmt(totalPrice)}`
                  : `Buy with ${coinLabel} \u00B7 $${fmt(totalPrice)}`}
              </span>
              <ArrowIcon />
            </MobileCta>

            {/* PAYMENT TABS */}
            <SecLbl>Payment Method</SecLbl>
            <PayTabs>
              <PayTab
                $active={method === "crypto"}
                $soon={!PAY_CFG.crypto.enabled}
                $a={a}
                onClick={() => {
                  if (!PAY_CFG.crypto.enabled) return;
                  setMethod("crypto");
                }}
              >
                <PtIcon><CryptoIcon /></PtIcon>
                <PtName>Crypto</PtName>
                <PtSub $active={method === "crypto"} $a={a}>
                  Available now
                </PtSub>
              </PayTab>

              <PayTab
                $active={method === "card"}
                $soon={!PAY_CFG.card.enabled}
                $a={a}
                onClick={() => {
                  if (!PAY_CFG.card.enabled) return;
                  setMethod("card");
                }}
              >
                {!PAY_CFG.card.enabled && <SoonPill>Coming soon</SoonPill>}
                <PtIcon><CardIcon /></PtIcon>
                <PtName>Credit Card</PtName>
                <PtSub $active={method === "card"} $a={a}>
                  {PAY_CFG.card.enabled ? "Available now" : "Coming soon"}
                </PtSub>
              </PayTab>

              <PayTab
                $active={method === "paypal"}
                $soon={!PAY_CFG.paypal.enabled}
                $a={a}
                onClick={() => {
                  if (!PAY_CFG.paypal.enabled) return;
                  setMethod("paypal");
                }}
              >
                {!PAY_CFG.paypal.enabled && <SoonPill>Coming soon</SoonPill>}
                <PtIcon><PaypalIcon /></PtIcon>
                <PtName>PayPal</PtName>
                <PtSub $active={method === "paypal"} $a={a}>
                  {PAY_CFG.paypal.enabled ? "Available now" : "Coming soon"}
                </PtSub>
              </PayTab>
            </PayTabs>

            {/* CRYPTO COIN SELECTION */}
            <CryptoBlock $open={method === "crypto"}>
              <CoinGrid>
                {COINS.map((c) => (
                  <CoinBtn
                    key={c}
                    $active={coin === c}
                    $color={COIN_COLORS[c] ?? "#888"}
                    $a={a}
                    onClick={() => setCoin(c)}
                  >
                    <CoinDot
                      $color={COIN_COLORS[c] ?? "#888"}
                      $active={coin === c}
                      $a={a}
                    />
                    {c}
                  </CoinBtn>
                ))}
              </CoinGrid>
            </CryptoBlock>

            {/* COMING SOON INFO */}
            <PayInfo $show={method === "card"}>
              Credit &amp; debit card payments are coming soon. Stay tuned.
            </PayInfo>
            <PayInfo $show={method === "paypal"}>
              PayPal checkout is coming soon. Stay tuned.
            </PayInfo>

            {/* TRUST SIGNALS */}
            <TrustList>
              {TRUST_ITEMS.map((t) => (
                <TrustItem key={t}>
                  <TIcon $a={a}>&#x2713;</TIcon>
                  <span>{t}</span>
                </TrustItem>
              ))}
            </TrustList>

            {/* DESKTOP CTA */}
            <PanelCta $a={a} onClick={handleCheckout} disabled={payment.busy}>
              <span>
                {!userInfo?.uid
                  ? `Login to Purchase \u2014 $${fmt(totalPrice)}`
                  : payment.busy
                  ? "Processing..."
                  : method === "crypto" && !payment.isWalletConnected
                  ? `Connect Wallet & Pay \u2014 $${fmt(totalPrice)}`
                  : `Continue with ${coinLabel} \u2014 $${fmt(totalPrice)}`}
              </span>
            </PanelCta>
          </CoPanel>
        </Shell>
      </Page>

      {/* ═══════ LOADING OVERLAY ═══════ */}
      {payment.busy && (
        <Backdrop $show style={{ cursor: "wait" }}>
          <ModalBox>
            <MHead>
              <MEmoji>&#x23F3;</MEmoji>
              <MTitle>Processing Payment...</MTitle>
              <MSub>Please confirm the transaction in your wallet</MSub>
            </MHead>
          </ModalBox>
        </Backdrop>
      )}

      {/* ═══════ ERROR MODAL ═══════ */}
      <Backdrop
        $show={payment.status.type === "error"}
        onClick={(e) => {
          if (e.target === e.currentTarget) payment.reset();
        }}
      >
        <ModalBox>
          <MClose onClick={payment.reset}>&times;</MClose>
          <MHead>
            <MEmoji>&#x26A0;&#xFE0F;</MEmoji>
            <MTitle>Transaction Failed</MTitle>
            <MSub>{payment.status.message}</MSub>
          </MHead>
          <MActions style={{ gridTemplateColumns: "1fr" }}>
            <MBtn $primary $a={a} onClick={payment.reset}>
              Try Again
            </MBtn>
          </MActions>
        </ModalBox>
      </Backdrop>

      {/* ═══════ SUCCESS MODAL ═══════ */}
      <Backdrop
        $show={payment.status.type === "success"}
        onClick={(e) => {
          if (e.target === e.currentTarget) payment.reset();
        }}
      >
        <ModalBox>
          <MClose onClick={payment.reset}>&times;</MClose>
          <MHead>
            <MEmoji>&#x1F389;</MEmoji>
            <MTitle>Purchase Complete!</MTitle>
            <MSub>
              {qty} &times; {item.name}
            </MSub>
          </MHead>
          <MCardWrap $a={a}>
            <MCardArt>
              <img src={mainImgSrc} alt="" />
            </MCardArt>
            <MCardOv>
              <MCardName>{item.name}</MCardName>
            </MCardOv>
          </MCardWrap>
          <MRewards>
            <MReward $a={a}>
              <MRLbl>Vcoin Added</MRLbl>
              <MRVal $a={a}>{fmt(totalVcoin)}</MRVal>
            </MReward>
            <MReward $a={a}>
              <MRLbl>Power Unlocked</MRLbl>
              <MRVal $a={a} style={{ fontSize: 13, letterSpacing: "-0.02em" }}>
                x{item.powerMult} &middot; {fmt(totalPower)} uses
              </MRVal>
            </MReward>
          </MRewards>
          <MCopy>
            Your collectible is now in <strong>My Space</strong>. Share it to
            apply for Social Boost and double your Vcoin.
          </MCopy>
          <MActions>
            <MBtn onClick={payment.goToPurchases}>View in My Space</MBtn>
            <MBtn
              $primary
              $a={a}
              onClick={() => {
                payment.reset();
                alert("Connect to your Social Boost share flow.");
              }}
            >
              Share &amp; Boost &times;2
            </MBtn>
          </MActions>
        </ModalBox>
      </Backdrop>
    </>
  );
};

export default BuyGetCheckout;
